import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, RotateCcw, Stamp } from "lucide-react";
import { waLink } from "@/data/site";

const QUESTIONS = [
  {
    key: "destination",
    title: "Qual é o seu destino?",
    options: ["Estados Unidos", "Cidadania Italiana", "Canadá", "Japão", "México", "China", "Austrália", "Outro"],
  },
  {
    key: "purpose",
    title: "Qual o propósito da viagem?",
    options: ["Turismo", "Negócios", "Estudos", "Visita a familiares"],
  },
  {
    key: "passport",
    title: "Como está seu passaporte?",
    options: ["Válido", "Vencido", "Ainda não tenho"],
  },
];

const getResult = (answers) => {
  let score = 0;
  if (answers.passport === "Válido") score += 2;
  if (answers.passport === "Vencido") score += 1;
  if (answers.purpose === "Turismo" || answers.purpose === "Negócios") score += 1;
  if (answers.destination !== "Outro") score += 1;
  if (score >= 4)
    return { label: "Prontidão Alta", desc: "Seu perfil está bem encaminhado. Com a montagem documental correta, suas chances são excelentes." };
  if (score === 3)
    return { label: "Prontidão Boa", desc: "Seu perfil é promissor — alguns ajustes estratégicos na documentação elevarão suas chances." };
  return { label: "Estratégia Personalizada", desc: "Seu caso pede um plano sob medida. Nossa equipe desenha a rota mais segura para o seu visto." };
};

export const Eligibility = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= QUESTIONS.length;
  const result = done ? getResult(answers) : null;

  const pick = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setStep((s) => s + 1);
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
  };

  return (
    <section id="analise" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="mb-3 font-mono-tech text-xs tracking-[0.3em] text-gold-400">
            03 — ANÁLISE DE PERFIL
          </p>
          <h2 className="font-serif-display text-3xl font-normal leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Descubra em 30 segundos o seu{" "}
            <em className="gold-text italic">nível de prontidão.</em>
          </h2>
          <p className="mt-5 max-w-md text-base font-light leading-relaxed text-slate-400">
            Responda três perguntas rápidas e receba uma leitura inicial do seu
            perfil consular — com encaminhamento direto para um especialista.
          </p>
        </motion.div>

        <motion.div
          data-testid="eligibility-analyzer"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative overflow-hidden rounded-2xl border border-gold-400/25 bg-navy-800/70 p-7 backdrop-blur-sm sm:p-9"
        >
          <div className="mb-6 flex items-center justify-between">
            <div className="flex gap-2">
              {QUESTIONS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 w-8 rounded-full transition-colors duration-500 ${
                    i < step ? "bg-gold-400" : "bg-slate-700"
                  }`}
                />
              ))}
            </div>
            <span className="font-mono-tech text-[10px] tracking-widest text-slate-500">
              {done ? "RESULTADO" : `ETAPA ${step + 1}/3`}
            </span>
          </div>

          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
              >
                <h3 className="font-serif-display mb-6 text-2xl text-slate-100">
                  {QUESTIONS[step].title}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {QUESTIONS[step].options.map((opt) => (
                    <button
                      key={opt}
                      data-testid={`eligibility-option-${QUESTIONS[step].key}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => pick(QUESTIONS[step].key, opt)}
                      className="rounded-full border border-slate-600/60 px-5 py-2.5 text-sm text-slate-200 transition-colors duration-300 hover:border-gold-400 hover:bg-gold-400/10 hover:text-gold-200"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                data-testid="eligibility-result"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="text-center"
              >
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-gold-400/50 bg-gold-400/10">
                  <Stamp className="h-7 w-7 text-gold-300" />
                </div>
                <p className="font-mono-tech text-[10px] tracking-[0.3em] text-slate-500">
                  LEITURA INICIAL DO PERFIL
                </p>
                <h3 className="font-serif-display gold-text mt-2 text-3xl">{result.label}</h3>
                <p className="mx-auto mt-4 max-w-sm text-sm font-light leading-relaxed text-slate-400">
                  {result.desc}
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <a
                    data-testid="eligibility-whatsapp-button"
                    href={waLink(`Olá! Fiz a análise de perfil no site. Destino: ${answers.destination}, Propósito: ${answers.purpose}, Passaporte: ${answers.passport}. Resultado: ${result.label}. Quero uma consultoria.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full bg-gold-400 px-6 py-3 text-sm font-semibold text-navy-950 transition-colors duration-300 hover:bg-gold-200"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Enviar análise no WhatsApp
                  </a>
                  <button
                    data-testid="eligibility-restart-button"
                    onClick={reset}
                    className="flex items-center gap-2 rounded-full border border-slate-600/60 px-6 py-3 text-sm text-slate-300 transition-colors duration-300 hover:border-gold-400/50 hover:text-gold-200"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Refazer
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
